import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import {
  getDeliveryTimes,
  updateDeliveryTimes,
  DeliveryTimes,
  getAllGlassOptions,
  createGlassOption,
  updateGlassOption,
  deleteGlassOption,
  getAllBandOptions,
  createBandOption,
  updateBandOption,
  deleteBandOption,
  GlassOption,
  BandOption,
} from '../services/admin';
import {
  getAllDealers,
  updateDealerDiscount,
  updateDealerActive,
  sendPasswordSetupEmail,
  Dealer,
} from '../services/dealers';
import './AdminSettingsPage.css';

const AdminSettingsPage: React.FC = () => {
  const [deliveryTimes, setDeliveryTimes] = useState<DeliveryTimes>({
    CLIP: 7,
    COMPLETE_GLASSES: 5,
  });
  const [editingClip, setEditingClip] = useState(false);
  const [editingComplete, setEditingComplete] = useState(false);
  const [clipValue, setClipValue] = useState('');
  const [completeValue, setCompleteValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Glass and Band Options
  const [glassOptions, setGlassOptions] = useState<GlassOption[]>([]);
  const [bandOptions, setBandOptions] = useState<BandOption[]>([]);
  const [editingGlassId, setEditingGlassId] = useState<string | null>(null);
  const [editingBandId, setEditingBandId] = useState<string | null>(null);
  const [newGlassName, setNewGlassName] = useState('');
  const [newGlassDescription, setNewGlassDescription] = useState('');
  const [newBandName, setNewBandName] = useState('');
  const [newBandDescription, setNewBandDescription] = useState('');

  // Accordion states
  const [expandedDeliveryTimes, setExpandedDeliveryTimes] = useState(false);
  const [expandedGlassOptions, setExpandedGlassOptions] = useState(false);
  const [expandedBandOptions, setExpandedBandOptions] = useState(false);
  const [expandedDealers, setExpandedDealers] = useState(false);

  // Dealers
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(null);
  const [discountValue, setDiscountValue] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    loadDeliveryTimes();
    loadGlassOptions();
    loadBandOptions();
    loadDealers();
  }, []);

  const loadDeliveryTimes = async () => {
    setLoading(true);
    setError(null);
    try {
      const times = await getDeliveryTimes();
      setDeliveryTimes(times);
    } catch (error: any) {
      console.error('Error loading delivery times:', error);
      const errorMessage =
        error.response?.data?.error || error.message || 'Fehler beim Laden der Lieferzeiten';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClip = async () => {
    const days = parseInt(clipValue, 10);
    if (isNaN(days) || days < 0) {
      setError('Bitte eine gültige Anzahl von Werktagen eingeben');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await updateDeliveryTimes({
        ...deliveryTimes,
        CLIP: days,
      });
      setDeliveryTimes(updated);
      setEditingClip(false);
      setClipValue('');
      setSuccess('Lieferzeit für Clip erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error updating delivery time:', error);
      const errorMessage =
        error.response?.data?.error || error.message || 'Fehler beim Aktualisieren der Lieferzeit';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveComplete = async () => {
    const days = parseInt(completeValue, 10);
    if (isNaN(days) || days < 0) {
      setError('Bitte eine gültige Anzahl von Werktagen eingeben');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await updateDeliveryTimes({
        ...deliveryTimes,
        COMPLETE_GLASSES: days,
      });
      setDeliveryTimes(updated);
      setEditingComplete(false);
      setCompleteValue('');
      setSuccess('Lieferzeit für Komplette Brille erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error updating delivery time:', error);
      const errorMessage =
        error.response?.data?.error || error.message || 'Fehler beim Aktualisieren der Lieferzeit';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const loadGlassOptions = async () => {
    try {
      const options = await getAllGlassOptions();
      setGlassOptions(options);
    } catch (error: any) {
      console.error('Error loading glass options:', error);
    }
  };

  const loadBandOptions = async () => {
    try {
      const options = await getAllBandOptions();
      setBandOptions(options);
    } catch (error: any) {
      console.error('Error loading band options:', error);
    }
  };

  const handleCreateGlass = async () => {
    if (!newGlassName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }
    setError(null);
    try {
      await createGlassOption({
        name: newGlassName.trim(),
        description: newGlassDescription.trim() || undefined,
        active: true,
      });
      setNewGlassName('');
      setNewGlassDescription('');
      await loadGlassOptions();
      setSuccess('Glas erfolgreich erstellt');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error creating glass option:', error);
      const errorMessage =
        error.response?.data?.error || error.message || 'Fehler beim Erstellen des Glases';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleUpdateGlass = async (
    id: string,
    name: string,
    description: string,
    active: boolean
  ) => {
    try {
      await updateGlassOption(id, {
        name: name.trim(),
        description: description.trim() || undefined,
        active,
      });
      setEditingGlassId(null);
      await loadGlassOptions();
      setSuccess('Glas erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren des Glases');
    }
  };

  const handleDeleteGlass = async (id: string) => {
    if (!window.confirm('Möchten Sie dieses Glas wirklich löschen?')) {
      return;
    }
    try {
      await deleteGlassOption(id);
      await loadGlassOptions();
      setSuccess('Glas erfolgreich gelöscht');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Löschen des Glases');
    }
  };

  const handleCreateBand = async () => {
    if (!newBandName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }
    try {
      await createBandOption({
        name: newBandName.trim(),
        description: newBandDescription.trim() || undefined,
        active: true,
      });
      setNewBandName('');
      setNewBandDescription('');
      await loadBandOptions();
      setSuccess('Band erfolgreich erstellt');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Erstellen des Bandes');
    }
  };

  const handleUpdateBand = async (
    id: string,
    name: string,
    description: string,
    active: boolean
  ) => {
    try {
      await updateBandOption(id, {
        name: name.trim(),
        description: description.trim() || undefined,
        active,
      });
      setEditingBandId(null);
      await loadBandOptions();
      setSuccess('Band erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren des Bandes');
    }
  };

  const handleDeleteBand = async (id: string) => {
    if (!window.confirm('Möchten Sie dieses Band wirklich löschen?')) {
      return;
    }
    try {
      await deleteBandOption(id);
      await loadBandOptions();
      setSuccess('Band erfolgreich gelöscht');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Löschen des Bandes');
    }
  };

  const loadDealers = async () => {
    try {
      const data = await getAllDealers();
      setDealers(data);
    } catch (error: any) {
      console.error('Error loading dealers:', error);
    }
  };

  const handleUpdateDiscount = async (id: string, discount: number) => {
    try {
      await updateDealerDiscount(id, discount);
      await loadDealers();
      setEditingDiscountId(null);
      setDiscountValue('');
      setSuccess('Rabatt erfolgreich aktualisiert');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren des Rabatts');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      await updateDealerActive(id, !currentActive);
      await loadDealers();
      setSuccess(`Händlerzugang erfolgreich ${!currentActive ? 'aktiviert' : 'deaktiviert'}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Ändern des Status');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSendPasswordEmail = async (id: string, email: string) => {
    if (!window.confirm(`Möchten Sie wirklich eine Passwort-Set-Mail an ${email} senden?`)) {
      return;
    }
    try {
      await sendPasswordSetupEmail(id);
      setSuccess('Passwort-Set-Mail wurde erfolgreich gesendet');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Fehler beim Senden der E-Mail');
      setTimeout(() => setError(null), 5000);
    }
  };

  return (
    <div>
      <AdminHeader />
      <div className="container">
        <h1>Einstellungen</h1>

        {loading ? (
          <div className="loading">Lade Einstellungen...</div>
        ) : (
          <>
            {error && (
              <div className="error" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            {success && (
              <div className="success" style={{ marginBottom: '1rem' }}>
                {success}
              </div>
            )}

            {/* Lieferzeiten Accordion */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '1rem',
                  backgroundColor: expandedDeliveryTimes ? '#f0f0f0' : 'transparent',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  margin: '-1rem -1rem 0 -1rem',
                }}
                onClick={() => setExpandedDeliveryTimes(!expandedDeliveryTimes)}
              >
                <h2 style={{ margin: 0 }}>Lieferzeiten</h2>
                <span style={{ fontSize: '1.5em', userSelect: 'none' }}>
                  {expandedDeliveryTimes ? '▼' : '▶'}
                </span>
              </div>
              {expandedDeliveryTimes && (
                <div style={{ paddingTop: '1rem' }}>
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Hier können Sie die geschätzten Lieferzeiten für die verschiedenen Produkttypen
                    anpassen.
                  </p>

                  <div style={{ marginBottom: '2rem' }}>
                    <h3>Clip</h3>
                    {editingClip ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="number"
                          value={clipValue}
                          onChange={e => setClipValue(e.target.value)}
                          placeholder={String(deliveryTimes.CLIP)}
                          min="0"
                          style={{ width: '100px', padding: '8px' }}
                          disabled={saving}
                        />
                        <span>Werktage</span>
                        <button
                          onClick={handleSaveClip}
                          className="btn btn-primary"
                          disabled={saving}
                        >
                          Speichern
                        </button>
                        <button
                          onClick={() => {
                            setEditingClip(false);
                            setClipValue('');
                          }}
                          className="btn btn-secondary"
                          disabled={saving}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <p style={{ margin: 0, fontSize: '1.1em' }}>
                          <strong>{deliveryTimes.CLIP} Werktage</strong>
                        </p>
                        <button
                          onClick={() => {
                            setEditingClip(true);
                            setClipValue(String(deliveryTimes.CLIP));
                          }}
                          className="btn btn-primary"
                        >
                          Bearbeiten
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3>Komplette Brille</h3>
                    {editingComplete ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                          type="number"
                          value={completeValue}
                          onChange={e => setCompleteValue(e.target.value)}
                          placeholder={String(deliveryTimes.COMPLETE_GLASSES)}
                          min="0"
                          style={{ width: '100px', padding: '8px' }}
                          disabled={saving}
                        />
                        <span>Werktage</span>
                        <button
                          onClick={handleSaveComplete}
                          className="btn btn-primary"
                          disabled={saving}
                        >
                          Speichern
                        </button>
                        <button
                          onClick={() => {
                            setEditingComplete(false);
                            setCompleteValue('');
                          }}
                          className="btn btn-secondary"
                          disabled={saving}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <p style={{ margin: 0, fontSize: '1.1em' }}>
                          <strong>{deliveryTimes.COMPLETE_GLASSES} Werktage</strong>
                        </p>
                        <button
                          onClick={() => {
                            setEditingComplete(true);
                            setCompleteValue(String(deliveryTimes.COMPLETE_GLASSES));
                          }}
                          className="btn btn-primary"
                        >
                          Bearbeiten
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Glass Options Accordion */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '1rem',
                  backgroundColor: expandedGlassOptions ? '#f0f0f0' : 'transparent',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                }}
                onClick={() => setExpandedGlassOptions(!expandedGlassOptions)}
              >
                <h2 style={{ margin: 0 }}>Gläser verwalten</h2>
                <span style={{ fontSize: '1.5em', userSelect: 'none' }}>
                  {expandedGlassOptions ? '▼' : '▶'}
                </span>
              </div>
              {expandedGlassOptions && (
                <div style={{ paddingTop: '1rem' }}>
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Hier können Sie die verfügbaren Glas-Optionen verwalten.
                  </p>

                  <div style={{ marginBottom: '2rem' }}>
                    <h3>Neues Glas hinzufügen</h3>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder="Name"
                        value={newGlassName}
                        onChange={e => setNewGlassName(e.target.value)}
                        style={{ flex: 1, padding: '8px' }}
                      />
                      <input
                        type="text"
                        placeholder="Beschreibung (optional)"
                        value={newGlassDescription}
                        onChange={e => setNewGlassDescription(e.target.value)}
                        style={{ flex: 1, padding: '8px' }}
                      />
                      <button onClick={handleCreateGlass} className="btn btn-primary">
                        Hinzufügen
                      </button>
                    </div>
                  </div>

                  <div>
                    <h3>Vorhandene Gläser</h3>
                    {glassOptions.length === 0 ? (
                      <p style={{ color: '#666' }}>Keine Gläser vorhanden</p>
                    ) : (
                      <table className="table" style={{ marginTop: '1rem' }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Beschreibung</th>
                            <th>Aktiv</th>
                            <th>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {glassOptions.map(option => (
                            <tr key={option.id}>
                              {editingGlassId === option.id ? (
                                <>
                                  <td>
                                    <input
                                      type="text"
                                      defaultValue={option.name}
                                      id={`glass-name-${option.id}`}
                                      style={{ width: '100%', padding: '4px' }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      defaultValue={option.description || ''}
                                      id={`glass-desc-${option.id}`}
                                      style={{ width: '100%', padding: '4px' }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="checkbox"
                                      defaultChecked={option.active}
                                      id={`glass-active-${option.id}`}
                                    />
                                  </td>
                                  <td>
                                    <button
                                      onClick={() => {
                                        const name =
                                          (
                                            document.getElementById(
                                              `glass-name-${option.id}`
                                            ) as HTMLInputElement
                                          )?.value || option.name;
                                        const desc =
                                          (
                                            document.getElementById(
                                              `glass-desc-${option.id}`
                                            ) as HTMLInputElement
                                          )?.value || '';
                                        const active =
                                          (
                                            document.getElementById(
                                              `glass-active-${option.id}`
                                            ) as HTMLInputElement
                                          )?.checked ?? option.active;
                                        handleUpdateGlass(option.id, name, desc, active);
                                      }}
                                      className="btn btn-primary"
                                      style={{ marginRight: '5px' }}
                                    >
                                      Speichern
                                    </button>
                                    <button
                                      onClick={() => setEditingGlassId(null)}
                                      className="btn btn-secondary"
                                    >
                                      Abbrechen
                                    </button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>{option.name}</td>
                                  <td>{option.description || '—'}</td>
                                  <td>{option.active ? 'Ja' : 'Nein'}</td>
                                  <td>
                                    <button
                                      onClick={() => setEditingGlassId(option.id)}
                                      className="btn btn-secondary"
                                      style={{ marginRight: '5px' }}
                                    >
                                      Bearbeiten
                                    </button>
                                    <button
                                      onClick={() => handleDeleteGlass(option.id)}
                                      className="btn btn-danger"
                                    >
                                      Löschen
                                    </button>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Band Options Accordion */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '1rem',
                  backgroundColor: expandedBandOptions ? '#f0f0f0' : 'transparent',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  margin: '-1rem -1rem 0 -1rem',
                }}
                onClick={() => setExpandedBandOptions(!expandedBandOptions)}
              >
                <h2 style={{ margin: 0 }}>Bänder verwalten</h2>
                <span style={{ fontSize: '1.5em', userSelect: 'none' }}>
                  {expandedBandOptions ? '▼' : '▶'}
                </span>
              </div>
              {expandedBandOptions && (
                <div style={{ paddingTop: '1rem' }}>
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Hier können Sie die verfügbaren Band-Optionen verwalten.
                  </p>

                  <div style={{ marginBottom: '2rem' }}>
                    <h3>Neues Band hinzufügen</h3>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder="Name"
                        value={newBandName}
                        onChange={e => setNewBandName(e.target.value)}
                        style={{ flex: 1, padding: '8px' }}
                      />
                      <input
                        type="text"
                        placeholder="Beschreibung (optional)"
                        value={newBandDescription}
                        onChange={e => setNewBandDescription(e.target.value)}
                        style={{ flex: 1, padding: '8px' }}
                      />
                      <button onClick={handleCreateBand} className="btn btn-primary">
                        Hinzufügen
                      </button>
                    </div>
                  </div>

                  <div>
                    <h3>Vorhandene Bänder</h3>
                    {bandOptions.length === 0 ? (
                      <p style={{ color: '#666' }}>Keine Bänder vorhanden</p>
                    ) : (
                      <table className="table" style={{ marginTop: '1rem' }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Beschreibung</th>
                            <th>Aktiv</th>
                            <th>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bandOptions.map(option => (
                            <tr key={option.id}>
                              {editingBandId === option.id ? (
                                <>
                                  <td>
                                    <input
                                      type="text"
                                      defaultValue={option.name}
                                      id={`band-name-${option.id}`}
                                      style={{ width: '100%', padding: '4px' }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      defaultValue={option.description || ''}
                                      id={`band-desc-${option.id}`}
                                      style={{ width: '100%', padding: '4px' }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="checkbox"
                                      defaultChecked={option.active}
                                      id={`band-active-${option.id}`}
                                    />
                                  </td>
                                  <td>
                                    <button
                                      onClick={() => {
                                        const name =
                                          (
                                            document.getElementById(
                                              `band-name-${option.id}`
                                            ) as HTMLInputElement
                                          )?.value || option.name;
                                        const desc =
                                          (
                                            document.getElementById(
                                              `band-desc-${option.id}`
                                            ) as HTMLInputElement
                                          )?.value || '';
                                        const active =
                                          (
                                            document.getElementById(
                                              `band-active-${option.id}`
                                            ) as HTMLInputElement
                                          )?.checked ?? option.active;
                                        handleUpdateBand(option.id, name, desc, active);
                                      }}
                                      className="btn btn-primary"
                                      style={{ marginRight: '5px' }}
                                    >
                                      Speichern
                                    </button>
                                    <button
                                      onClick={() => setEditingBandId(null)}
                                      className="btn btn-secondary"
                                    >
                                      Abbrechen
                                    </button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>{option.name}</td>
                                  <td>{option.description || '—'}</td>
                                  <td>{option.active ? 'Ja' : 'Nein'}</td>
                                  <td>
                                    <button
                                      onClick={() => setEditingBandId(option.id)}
                                      className="btn btn-secondary"
                                      style={{ marginRight: '5px' }}
                                    >
                                      Bearbeiten
                                    </button>
                                    <button
                                      onClick={() => handleDeleteBand(option.id)}
                                      className="btn btn-danger"
                                    >
                                      Löschen
                                    </button>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Dealers Management Accordion */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '1rem',
                  backgroundColor: expandedDealers ? '#f0f0f0' : 'transparent',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  margin: '-1rem -1rem 0 -1rem',
                }}
                onClick={() => setExpandedDealers(!expandedDealers)}
              >
                <h2 style={{ margin: 0 }}>Händler verwalten</h2>
                <span style={{ fontSize: '1.5em', userSelect: 'none' }}>
                  {expandedDealers ? '▼' : '▶'}
                </span>
              </div>
              {expandedDealers && (
                <div style={{ paddingTop: '1rem' }}>
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    Hier können Sie Händlerzugänge aktivieren/deaktivieren und Rabatte verwalten.
                  </p>

                  {dealers.length === 0 ? (
                    <p style={{ color: '#666' }}>Keine Händler vorhanden</p>
                  ) : (
                    <table className="table" style={{ marginTop: '1rem' }}>
                      <thead>
                        <tr>
                          <th>Benutzername</th>
                          <th>E-Mail</th>
                          <th>Status</th>
                          <th>Rabatt</th>
                          <th>Bestellungen</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dealers.map(dealer => (
                          <tr key={dealer.id}>
                            <td>{dealer.username}</td>
                            <td>{dealer.email}</td>
                            <td>
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: dealer.active !== false ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontSize: '0.9em',
                                }}
                              >
                                {dealer.active !== false ? 'Aktiv' : 'Deaktiviert'}
                              </span>
                            </td>
                            <td>
                              {editingDiscountId === dealer.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <input
                                    type="number"
                                    value={discountValue}
                                    onChange={e => setDiscountValue(e.target.value)}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    style={{ width: '80px', padding: '4px' }}
                                    placeholder={String(dealer.discountPercent || 0)}
                                  />
                                  <span>%</span>
                                  <button
                                    onClick={() => {
                                      const discount = parseFloat(discountValue);
                                      if (!isNaN(discount) && discount >= 0 && discount <= 100) {
                                        handleUpdateDiscount(dealer.id, discount);
                                      } else {
                                        setError('Rabatt muss zwischen 0 und 100 liegen');
                                        setTimeout(() => setError(null), 3000);
                                      }
                                    }}
                                    className="btn btn-primary"
                                    style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                  >
                                    Speichern
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingDiscountId(null);
                                      setDiscountValue('');
                                    }}
                                    className="btn btn-secondary"
                                    style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                  >
                                    Abbrechen
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <span>{dealer.discountPercent || 0}%</span>
                                  <button
                                    onClick={() => {
                                      setEditingDiscountId(dealer.id);
                                      setDiscountValue(String(dealer.discountPercent || 0));
                                    }}
                                    className="btn btn-secondary"
                                    style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                  >
                                    Bearbeiten
                                  </button>
                                </div>
                              )}
                            </td>
                            <td>{dealer._count?.orders || 0}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() =>
                                    handleToggleActive(dealer.id, dealer.active !== false)
                                  }
                                  className={
                                    dealer.active !== false ? 'btn btn-danger' : 'btn btn-success'
                                  }
                                  style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                >
                                  {dealer.active !== false ? 'Deaktivieren' : 'Aktivieren'}
                                </button>
                                <button
                                  onClick={() => handleSendPasswordEmail(dealer.id, dealer.email)}
                                  className="btn btn-primary"
                                  style={{ padding: '4px 8px', fontSize: '0.9em' }}
                                >
                                  Passwort-Mail
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSettingsPage;
